import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { FormTemplate, PaginatedFormResponses } from '../interfaces/forms';
import { exportFormResponsesCsv, getFormResponses, getFormTemplate } from '../../../api/formsApi';
import FullScreenLoader from '../../../components/loading/FullScreenLoader';
import '../formularios.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface FormResponsesPageProps {
  templateId: string;
  templateTitle: string;
  onClose: () => void;
}

const LIMIT = 20;

const FormResponsesPage: React.FC<FormResponsesPageProps> = ({ templateId, templateTitle, onClose }) => {
  const [template, setTemplate] = useState<FormTemplate | null>(null);
  const [data, setData] = useState<PaginatedFormResponses | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    getFormTemplate(templateId)
      .then(setTemplate)
      .catch(() => toast.error('Erro ao carregar formulário'));
  }, [templateId]);

  useEffect(() => {
    setLoading(true);
    getFormResponses(templateId, page, LIMIT)
      .then(setData)
      .catch(() => toast.error('Erro ao carregar respostas'))
      .finally(() => setLoading(false));
  }, [templateId, page]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportFormResponsesCsv(templateId, templateTitle);
    } catch (err) {
      toast.error('Erro ao exportar respostas');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / LIMIT)) : 1;

  const formatValue = (value: string | undefined, fieldType?: string) => {
    if (value === undefined || value === '') return '-';
    if (fieldType === 'MULTIPLE_CHOICE') return value.split('||').join(', ');
    return value;
  };

  return (
    <>
      {exporting && <FullScreenLoader />}
      <div className="formularios-modal-overlay" onClick={onClose}>
        <div className="formularios-modal formularios-modal-large" onClick={(e) => e.stopPropagation()}>
          <div className="formularios-modal-header">
            <h2 className="formularios-modal-title">Respostas - {templateTitle}</h2>
            <button className="close-button" type="button" onClick={onClose}>&times;</button>
          </div>

          <div className="formularios-modal-body">
            <div className="formularios-card-actions" style={{ marginBottom: 16 }}>
              <button className="formularios-btn primary" type="button" onClick={handleExport} disabled={exporting || (data?.total ?? 0) === 0}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Exportar CSV
              </button>
            </div>

            {loading && (
              <div className="formularios-modal-loading">
                <div className="spinner" />
                <p>Carregando respostas...</p>
              </div>
            )}

            {!loading && data && data.items.length === 0 && (
              <div className="formularios-empty">Nenhuma resposta registrada ainda.</div>
            )}

            {!loading && data && data.items.length > 0 && template && (
              <div className="formularios-table-wrapper">
                <table className="formularios-table">
                  <thead>
                    <tr>
                      <th className="th-nowrap">Data/Hora</th>
                      {template.requiresCar && <th className="th-nowrap">Veículo</th>}
                      <th className="th-nowrap">Respondido por</th>
                      {template.fields.map((field) => (
                        <th key={field.id} className="th-question">{field.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((response) => {
                      const answersByFieldId = new Map(response.answers.map((answer) => [answer.fieldId, answer.value]));
                      return (
                        <tr key={response.id}>
                          <td className="td-nowrap">{new Date(response.createdAt).toLocaleString('pt-BR')}</td>
                          {template.requiresCar && (
                            <td className="td-nowrap">
                              {response.car ? (
                                <>
                                  <span style={{ color: '#ea5d0e', fontWeight: 700, fontFamily: 'AmpleSoft-Bold, sans-serif' }}>
                                    {response.car.plate}
                                  </span>
                                  <span> - {response.car.model}</span>
                                </>
                              ) : (
                                '-'
                              )}
                            </td>
                          )}
                          {/*
                            `respondidoPor` vem pronto do backend (mesma regra do CSV) e é o que
                            distingue a resposta do ASSOCIADO, que não tem `respondedBy` e apareceria
                            como resposta sem autor. O fallback cobre resposta carregada antes do
                            backend novo subir.
                          */}
                          <td className="td-nowrap">
                            {response.respondidoPor
                              || response.respondedBy?.fullName
                              || response.respondedBy?.username
                              || '-'}
                          </td>
                          {template.fields.map((field) => (
                            <td key={field.id}>{formatValue(answersByFieldId.get(field.id || ''), field.type)}</td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {!loading && data && data.total > LIMIT && (
            <div className="formularios-pagination">
              <button className="formularios-btn" type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}>
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Anterior
              </button>
              <span>Página {page} de {totalPages}</span>
              <button className="formularios-btn" type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                Próxima
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4 }}>
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FormResponsesPage;
